/**
 * PendingAiReviewStore
 *
 * Coordinates the "ready-to-review" UI surfaces across the matter layout
 * (the polling hook fires the toast and tracks trigger source) and the
 * Documents tab (banner + the AIDocumentImportContainer that opens at the
 * review step).
 *
 * - `lastTriggerSource` controls the nothing-found behaviour: a typed-modal
 *   upload (`'core'`) surfaces a subtle toast on nothing-found, an opt-in
 *   Additional Documents upload (`'additional-opt-in'`) stays silent.
 *   Cleared back to `null` once the toast for that run has fired.
 *
 * - `reviewRequestedFor` is a matterId the user has asked to review (via
 *   toast action or banner click). DocumentsTabContent watches this and
 *   opens AIDocumentImportContainer in review-step mode, then clears the
 *   field. It is set BEFORE navigation when the request comes from another
 *   tab (VALE-1434), so DocumentsTabContent has to honour a value that is
 *   already there when it mounts, not only one that changes under it.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type AnalysisTriggerSource = 'core' | 'additional-opt-in';

interface PendingAiReviewState {
  lastTriggerSource: AnalysisTriggerSource | null;
  reviewRequestedFor: number | null;
  /**
   * A matterId whose extraction progress is already on screen, inside the
   * Extract dialog (VALE-757).
   *
   * The polling hook lives at the matter layout and knows nothing about what
   * the Documents tab is showing, so without this it fires its background
   * spinner on top of the inline progress the lawyer is already watching —
   * announcing as background work the very thing they are looking at. Cleared
   * when the dialog closes, at which point the spinner is wanted again because
   * the run really has gone back to being invisible.
   */
  inlineProgressFor: number | null;
  /**
   * A matterId whose completed-extraction result already has a surface on
   * screen: the Documents tab, whose `PendingAiReviewBanner` is the single
   * place a found-something result is announced (VALE-757).
   *
   * The layout polling hook cannot see which tab the lawyer is on, and until
   * VALE-1434 it assumed that banner. A grant uploaded from the GRANT tab with
   * extraction asked for therefore completed into silence: the in-flight toast
   * was dismissed and the only surface that would have spoken was on a tab the
   * lawyer was not looking at. This is what lets the hook tell the two apart,
   * so the fallback announcement fires where there is nothing to announce it
   * and stays quiet where the banner already does the job. Same shape and same
   * reasoning as `inlineProgressFor` above.
   */
  resultSurfaceFor: number | null;
}

interface PendingAiReviewActions {
  setTriggerSource: (source: AnalysisTriggerSource) => void;
  clearTriggerSource: () => void;
  requestReview: (matterId: number) => void;
  clearReviewRequest: () => void;
  setInlineProgress: (matterId: number | null) => void;
  setResultSurface: (matterId: number | null) => void;
}

type PendingAiReviewStore = PendingAiReviewState & PendingAiReviewActions;

export const usePendingAiReviewStore = create<PendingAiReviewStore>()(
  devtools(
    (set) => ({
      lastTriggerSource: null,
      reviewRequestedFor: null,
      setTriggerSource: (source) =>
        set({ lastTriggerSource: source }, false, 'setTriggerSource'),
      clearTriggerSource: () =>
        set({ lastTriggerSource: null }, false, 'clearTriggerSource'),
      requestReview: (matterId) =>
        set({ reviewRequestedFor: matterId }, false, 'requestReview'),
      clearReviewRequest: () =>
        set({ reviewRequestedFor: null }, false, 'clearReviewRequest'),
      inlineProgressFor: null,
      setInlineProgress: (matterId) =>
        set({ inlineProgressFor: matterId }, false, 'setInlineProgress'),
      resultSurfaceFor: null,
      setResultSurface: (matterId) =>
        set({ resultSurfaceFor: matterId }, false, 'setResultSurface')
    }),
    {
      name: 'PendingAiReviewStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);
